import React from 'react';
import { Alert, Autocomplete, Box, Button, Grid, TextField, Typography, FormControlLabel, Checkbox } from '@mui/material';
import CustomTextField from '../../CustomTextField';
import TextArea from '../../TextArea';
import CustomDropdown from '../../../components/CustomDropdownWithPlus';
import CustomDatePicker from '../../CustomDatePicker';
import SearchableAddressDropdown from './SearchableAddressDropdown';
import dayjs from 'dayjs';
import { shipToOptions, shippingAccountOptions, shippingMethodOptions, decoratorShipToOptions } from './index.controller.js';
import ThirdPartyShippingAccount from '../ThirdPartyShippingAccount/index.js';
import { decoratorAddressLines } from './decoratorDestinationRules';

const labelStyle = {
  fontSize: '14px !important',
  fontFamily: 'Nunito !important',
  color: '#555 !important'
};

const radioStyle = {
  alignItems: 'center',
  fontSize: '14px !important',
  fontFamily: 'Nunito !important',
  color: '#555 !important',
  width: '50%',
  display: 'flex',
  justifyContent: 'space-between',
  whiteSpace: 'noWrap'
};

const DecoratorShippingForm = ({
  decoratorFormData,
  setDecoratorFormData,
  decoratorShipToOptions,
  selectedShipTo,
  handleShipToChange,
  decoratorAddress,
  shippingAccountOptions,
  shippingMethodOptions: shippingMethodOptionsProp,
  decoratorOptions,
  dayjs,
  ourAccounts,
  supplierAccounts,
  clientAccount,
  setClientAccounts,
  setSupplierAccounts,
  AccountModalOpen,
  closeAccountModal,
  openAccountModal,
  customerId,
  supplierId,
  supplierName,
  customerDisplayName,
  instanceUniqueId,
  customerAddressOptions = [],
  companyAddressOptions = [],
  instanceDisplayName = 'Our Company',
  onDecoratorChange,
  receivingAddressLocked = false,
  receivingAddressError = '',
  configuredAddress = null,
  selectedDestination = null,
  onApplyConfiguredAddress,
}) => {
  const [accountModalContext, setAccountModalContext] = React.useState({ type: 'customer' });
  const resolvedShippingMethodOptions = shippingMethodOptionsProp || shippingMethodOptions;
  const selectedDecorator = decoratorOptions.find((option) =>
    String(option.id) === String(decoratorFormData.decorator)
  ) || null;
  const selectedReceivingAddress = decoratorAddress.find((option) =>
    String(option.id) === String(decoratorFormData.decoratorAddress)
  );
  const canApplyConfiguredAddress = configuredAddress &&
    selectedDestination?.rule?.visibleInShipping !== false &&
    String(configuredAddress.id) !== String(decoratorFormData.decoratorAddress);

  // Normalize legacy firm fields -> decoratorFirm once on mount
  React.useEffect(() => {
    if (typeof decoratorFormData?.decoratorFirm === 'undefined') {
      const legacy = (decoratorFormData?.isFirm ?? decoratorFormData?.firm ?? false);
      setDecoratorFormData(prev => ({ ...prev, decoratorFirm: !!legacy }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Do not clear a saved client destination while customer address options are
  // loading or when it is a valid legacy Address ID not present in the customer list.

  const handleInputChange = (event, name) => {
    const { value } = event.target;
    setDecoratorFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChange = (value, name) => {
    const next = value?.target?.value ?? value;
    setDecoratorFormData(prev => ({
      ...prev,
      [name]: next,
      ...(name === 'decoratorShipTo' && next === 'elite' && !prev.companyAddress && companyAddressOptions.length
        ? { companyAddress: companyAddressOptions.find((a) => a.isDefault)?.id || companyAddressOptions[0].id }
        : {}),
    }));
  };

  const handleShippingAccountChange = (eventOrValue) => {
    const value =
      eventOrValue?.target?.value ??
      eventOrValue?.id ??
      eventOrValue ??
      '';

    setDecoratorFormData((prev) => ({
      ...prev,
      shippingAccount: value,
      shippingUser: '',
      manualShippingAccount: value === 'other' ? prev.manualShippingAccount || '' : '',
    }));
  };

  const handleDateChange = (newDate, name) => {
    setDecoratorFormData(prev => ({ ...prev, [name]: newDate }));
  };

  const openCustomerAccountModal = () => {
    if (!customerId) return;
    openAccountModal('customer');
  };
  
  const openSupplierAccountModal = () => {
    if (!supplierId) return;
    openAccountModal('supplier');
  };  

  return (
    <>
      <Grid
        item
        xs={6}
        sx={{
          pl: 2,
          backgroundColor: '#e6edf5',
          padding: '0px 20px',
          marginLeft: '0px',
          borderRadius: '5px'
        }}
      >
        <Box className="field">
          <Typography sx={labelStyle}>Decorator</Typography>
          <Autocomplete
            fullWidth
            size="small"
            options={decoratorOptions}
            value={selectedDecorator}
            onChange={(_event, option) => onDecoratorChange
              ? onDecoratorChange(option?.id || '') : handleChange(option?.id || '', 'decorator')}
            getOptionLabel={(option) => `${option.name || 'Decorator'}${option.rule?.visibleInShipping === false ? ' (hidden)' : ''}`}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            getOptionDisabled={(option) => option.savedOnly === true || option.rule?.visibleInShipping === false}
            renderInput={(params) => (
              <TextField {...params} name="decorator" placeholder="Select Decorator" inputProps={{ ...params.inputProps, 'aria-label': 'Decorator' }} sx={{ backgroundColor: '#fff' }} />
            )}
          />
        </Box>

        <Box className="field">
          {receivingAddressLocked ? (
            <Box>
              <Typography sx={labelStyle}>Decorator Receiving Address</Typography>
              <Box sx={{ p: 1.25, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {selectedReceivingAddress?.recipientName || selectedReceivingAddress?.name ||
                    (decoratorFormData.decoratorAddress ? 'Saved receiving address' : 'No receiving address configured')}
                </Typography>
                {decoratorAddressLines(selectedReceivingAddress).map((line, index) => (
                  <Typography key={index} variant="body2">{line}</Typography>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Receiving address is locked for sales reps by the instance shipping rule.
              </Typography>
            </Box>
          ) : (
            <SearchableAddressDropdown
              name="decoratorAddress"
              options={decoratorAddress}
              onSelect={(id) => handleChange(id, 'decoratorAddress')}
              selectedValue={decoratorFormData.decoratorAddress}
              placeholder="Search decorator addresses"
              label={configuredAddress || decoratorFormData.decoratorAddress
                ? "Decorator Receiving Address"
                : "Decorator Receiving Address (optional)"}
            />
          )}
          {!receivingAddressLocked && selectedReceivingAddress && (
            <Box sx={{ mt: 0.75, px: 1.25, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Selected receiving address
              </Typography>
              {decoratorAddressLines(selectedReceivingAddress).length ? (
                decoratorAddressLines(selectedReceivingAddress).map((line, index) => (
                  <Typography key={index} variant="body2">{line}</Typography>
                ))
              ) : (
                <Typography variant="body2" color="warning.main">
                  No physical address details available. Select a complete receiving address.
                </Typography>
              )}
            </Box>
          )}
          {canApplyConfiguredAddress && (
            <Button size="small" onClick={onApplyConfiguredAddress} sx={{ mt: 0.5 }}>Use configured address</Button>
          )}
          {receivingAddressError ? (
            <Alert severity="warning" sx={{ mt: 1 }}>{receivingAddressError}</Alert>
          ) : selectedDecorator && !configuredAddress && !decoratorFormData.decoratorAddress &&
              !selectedDestination?.savedOnly ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              No receiving address is configured for this decorator yet. You can save Shipping without one.
              Purchasing will still need the correct receiving address before issuing the supplier PO.
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Typography sx={{ ...labelStyle, minWidth: '80px', mt: 0.5 }}>Ship To</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '80%' }}>
            {decoratorShipToOptions.map((option) => (
              <FormControlLabel
                key={option.id}
                control={
                  <input
                    type="radio"
                    name="decoratorShipTo"
                    value={option.name}
                    checked={decoratorFormData.decoratorShipTo === option.id}
                    onChange={() => handleChange(option.id, 'decoratorShipTo')}
                    style={{ width: '50%', minWidth: '100px' }}
                  />
                }
                label={option.id === 'elite' ? instanceDisplayName : option.name}
                sx={radioStyle}
              />
            ))}
          </Box>
        </Box>

        {decoratorFormData.decoratorShipTo === 'client' && (
          <Box className="field">
            <SearchableAddressDropdown
              name="clientAddress"
              options={customerAddressOptions}
              onSelect={(id) => handleChange(id, 'clientAddress')}
              selectedValue={decoratorFormData.clientAddress}
              placeholder="Search client addresses"
              label="Client Address"
            />
          </Box>
        )}
        {decoratorFormData.decoratorShipTo === 'elite' && (
          <Box className="field">
            <SearchableAddressDropdown
              name="companyAddress"
              options={companyAddressOptions}
              onSelect={(id) => handleChange(id, 'companyAddress')}
              selectedValue={decoratorFormData.companyAddress}
              placeholder="Search company receiving addresses"
              label={`${instanceDisplayName} Receiving Address`}
            />
            {!companyAddressOptions.length && (
              <Typography variant="caption" color="error">
                Add a receiving address in Company Contact Info before routing to this company.
              </Typography>
            )}
          </Box>
        )}

        <Box className="field">
          <CustomDatePicker
            isRequired
            label="In Hands Date (Decorator PO)"
            minValue={dayjs()}
            value={decoratorFormData.inHandsDate}
            onChange={(date) => handleDateChange(date, 'inHandsDate')}
          />
          {/* NEW: Firm (Decorator PO) */}
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={!!decoratorFormData.decoratorFirm}
                onChange={(e) => handleChange(e.target.checked, 'decoratorFirm')}
                color="primary"
              />
            }
            label="Firm (Decorator PO)"
          />
          {/* 
            Persist this with your save:
            ShipTo: { Type: 'decorator', InhandsDate: decoratorFormData.inHandsDate, IsFirm: decoratorFormData.decoratorFirm }
            or include inside your decoratorFormData payload for the PO.
          */}
        </Box>

        <Box>
          <Box className="field">
            {
            //JSON.stringify(decoratorFormData)
          }
            <CustomDropdown
              name="shippingAccount"
              options={shippingAccountOptions}
              handleSelect={handleShippingAccountChange}
              minWidth="150px"
              selectedValue={decoratorFormData.shippingAccount}
              placeholder="Select Shipping Account"
              label="Shipping Account"
            />
          </Box>

          {decoratorFormData.shippingAccount === 'other' && (
            <Box className="field">
              <CustomTextField
                name="manualShippingAccount"
                label="Other Shipping Account"
                placeholder="Enter shipping account or arrangement"
                value={decoratorFormData.manualShippingAccount || ''}
                isRequired
                handleChange={(event) => handleInputChange(event, 'manualShippingAccount')}
              />
            </Box>
          )}

          {(decoratorFormData.shippingAccount === 'our' || decoratorFormData.shippingAccount === 'client') && (
            <Box className="field">
              <CustomDropdown
                name="shippingUser"
                options={decoratorFormData.shippingAccount === 'our' ? ourAccounts : clientAccount}
                handleSelect={(e) => handleChange(e, 'shippingUser')}
                minWidth="150px"
                selectedValue={decoratorFormData.shippingUser}
                placeholder="Select Account"
                addPlusIcon={decoratorFormData.shippingAccount === 'client' && Boolean(customerId)}
                onPlusClick={openCustomerAccountModal}
              />
            </Box>
          )}

          {decoratorFormData.shippingAccount === 'supplier' && (
            <Box className="field">
              <CustomDropdown
                name="shippingUser"
                options={supplierAccounts}
                handleSelect={(e) => handleChange(e, 'shippingUser')}
                minWidth="150px"
                selectedValue={decoratorFormData.shippingUser}
                placeholder="Select Supplier Account"
                addPlusIcon={decoratorFormData.shippingAccount === 'supplier' && Boolean(supplierId)}
                onPlusClick={openSupplierAccountModal}
              />
            </Box>
          )}
        </Box>

        <Box className="field">
          <CustomDropdown
            name="shippingMethod"
            options={resolvedShippingMethodOptions}
            handleSelect={(e) => handleChange(e, 'shippingMethod')}
            minWidth="150px"
            selectedValue={decoratorFormData.shippingMethod}
            placeholder="Select Shipping Method"
            label="Shipping Method"
          />
        </Box>

        <Box className="field">
          <TextArea
            isRequired
            name="notes"
            label="Decorator Notes"
            value={decoratorFormData.notes}
            handleInputChange={(e) => handleInputChange(e, 'notes')}
          />
        </Box>
      </Grid>
    </>
  );
};

export default DecoratorShippingForm;
